/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useMutation } from 'react-query'

import {
  restoreElasticsearchProject,
  restoreObservabilityProject,
  restoreSecurityProject,
  restoreWorkplaceAIProject,
} from '@modules/project-admin-api/v1/callers'
import type { ProjectId } from '@modules/project-admin-api/v1/types'
import { queryClient } from '@modules/query'
import type { ProjectTypeWithWorkplaceAI } from '@modules/ui-types/projects'

import { makeQueryKey } from './helpers'

import type { UseMutationResult } from 'react-query'

const restoreCallers = {
  elasticsearch: restoreElasticsearchProject,
  observability: restoreObservabilityProject,
  security: restoreSecurityProject,
  workplaceai: restoreWorkplaceAIProject,
}

export function useRestoreProjectMutation<T extends ProjectTypeWithWorkplaceAI>(
  projectType: T,
): UseMutationResult<void, unknown, { id: ProjectId }> {
  const restoreCaller = restoreCallers[projectType]

  return useMutation({
    mutationFn: ({ id }: Parameters<typeof restoreCaller>[0]['pathParameters']) =>
      restoreCaller({
        pathParameters: { id },
      }),
    onSuccess: (_, { id }) => onProjectRestored(projectType, id),
  })
}

const onProjectRestored = (type: ProjectTypeWithWorkplaceAI, id: ProjectId) => {
  const listQueryKey = makeQueryKey(
    (
      {
        elasticsearch: 'adminListElasticsearchProjects',
        observability: 'adminListObservabilityProjects',
        security: 'adminListSecurityProjects',
        workplaceai: 'adminListWorkplaceAIProjects',
      } as const
    )[type],
  )
  queryClient.invalidateQueries(listQueryKey)

  const detailQueryKey = makeQueryKey(
    (
      {
        elasticsearch: 'adminGetElasticsearchProject',
        observability: 'adminGetObservabilityProject',
        security: 'adminGetSecurityProject',
        workplaceai: 'adminGetWorkplaceAIProject',
      } as const
    )[type],
    id,
  )
  queryClient.invalidateQueries(detailQueryKey)
}
